import { differenceInYears, isAfter, isBefore, isFuture, isPast, isSameDay, isValid, subDays } from "date-fns";
import i18n from "i18next";
import { isValidPhoneNumber } from "libphonenumber-js/max";
import get from "lodash/get";
import * as Yup from "yup";

import { defaultCountry } from "@/core/phone";
import { fakeI18n } from "@/i18n";
import * as dataCheckApi from "@/shared/apis/dataCheckApi";

import { convertHexToSixDigit, hexToHSL, HSL, hslToHex } from "./color";
import { defaultDateFormat } from "./date";

Yup.setLocale({
    mixed: {
        required: () => i18n.t("Validations.Required"),
        notType: ({ type }) => (type === "date" ? i18n.t("Validations.Date") : i18n.t("Validations.Format")),
    },
    string: {
        min: ({ min }) => i18n.t("Validations.MinLength", { number: min }),
        max: ({ max }) => i18n.t("Validations.MaxLength", { number: max }),
        email: () => i18n.t("Validations.Email"),
    },
    date: {
        min: ({ min }) => i18n.t("Validations.MinDate", { number: defaultDateFormat(min as Date) }),
        max: ({ max }) => i18n.t("Validations.MaxDate", { number: defaultDateFormat(max as Date) }),
    },
    number: {
        min: ({ min }) => i18n.t("Validations.MinValue", { minValue: min }),
        max: ({ max }) => i18n.t("Validations.MaxValue", { maxValue: max }),
    },
});

type ConditionalSchema<T> = T extends string
    ? Yup.StringSchema
    : T extends number
    ? Yup.NumberSchema
    : T extends boolean
    ? Yup.BooleanSchema
    : T extends Date
    ? Yup.DateSchema<any, any, any, any>
    : T extends Array<any>
    ? Yup.ArraySchema<any, any, any, any>
    : T extends Record<any, any>
    ? Yup.AnyObjectSchema
    : Yup.AnySchema;

export type Shape<Fields> = {
    [Key in keyof Fields]?: ConditionalSchema<Fields[Key]>;
};

type HexProps = {
    minimalSaturation?: number;
    minimalLightness?: number;
    maximalLightness?: number;
};

declare module "yup" {
    // @ts-ignore
    export interface Schema<TType = any, TContext = any, TDefault = any, TFlags extends Yup.Flags = "">
        extends Yup.Schema<TType, TContext, TDefault, TFlags> {
        /**
         * Make it required if any of the other values exist.
         */
        requiredIf(fields: string | string[]): this;
        /**
         * Make it required if all referenced fields are empty.
         */
        requiredIfEmpty(fields: string | string[]): this;
    }

    export type MixedSchema<
        TType extends Yup.Maybe<{}> = {} | undefined,
        TContext = Yup.AnyObject,
        TDefault = undefined,
        TFlags extends Yup.Flags = "",
    > = Schema<TType, TContext, TDefault, TFlags>;

    export interface StringSchema<
        TType extends Yup.Maybe<string> = string | undefined,
        TContext = Yup.AnyObject,
        TDefault = undefined,
        TFlags extends Yup.Flags = "",
    > extends Schema<TType, TContext, TDefault, TFlags> {
        /**
         * Check for phone number validity.
         */
        phone(): this;
        /**
         * Check for hexadecimal color validity.
         */
        hex(params?: HexProps): this;
        /**
         * Check for bce validity.
         */
        bceNumber(): this;
        /**
         * If value exists verify for length size.
         */
        exactLength(length: number): this;
    }

    export interface NumberSchema<
        TType extends Yup.Maybe<number> = number | undefined,
        TContext = Yup.AnyObject,
        TDefault = undefined,
        TFlags extends Yup.Flags = "",
    > extends Schema<TType, TContext, TDefault, TFlags> {
        /**
         * Check if comply to brio amount rules (euros).
         * default limit: { min: 0, max: 100000000 }
         */
        amount(limit?: { min: number; max: number }): this;
        /**
         * Transform currency string to number.
         */
        currencyTransform(): this;
    }

    export interface DateSchema<
        TType extends Yup.Maybe<Date>,
        TContext = Yup.AnyObject,
        TDefault = undefined,
        TFlags extends Yup.Flags = "",
    > extends Schema<TType, TContext, TDefault, TFlags> {
        /**
         * Verify if date is present or past.
         */
        presentOrPast(): this;
        /**
         * Verify if date is on future.
         */
        onFuture(): this;
        /**
         * Verify if birthDate is in plausible range.
         */
        birthdate(): this;
        /**
         * Verify if date year is prior to X years.
         */
        onLastXYears(years: number): this;
        /**
         * Verify if date is min X years old.
         */
        minXYearsOld(years: number, dateToCompare?: Date | string, message?: string): this;
        /**
         * Verify if the date is minimum to the next day of date provided .
         */
        minDayAfter(dateRef: keyof TContext, options?: { allowSameDay: boolean }): this;
    }

    export interface ArraySchema<
        TIn extends any[] | null | undefined,
        TContext,
        TDefault = undefined,
        TFlags extends Yup.Flags = "",
    > extends Schema<TIn, TContext, TDefault, TFlags> {
        /**
         * Verify if array values are unique.
         * e.g: .array().unique("name", 18n.t("Common.Name"))
         */
        unique(field: string, fieldLabel: string): this;
    }

    export interface ObjectSchema<
        TIn extends Yup.Maybe<Yup.AnyObject>,
        TContext = Yup.AnyObject,
        TDefault = any,
        TFlags extends Yup.Flags = "",
    > extends Schema<TIn, TContext, TDefault, TFlags> {}
}

// Yup mixed

// Any other field is present validator
const YUP_MIXED_REQUIRED_IF = "requiredIf";

//@ts-ignore
// https://github.com/jquense/yup/issues/2058
Yup.addMethod(Yup.Schema, YUP_MIXED_REQUIRED_IF, function requiredIf(fields: string | string[]) {
    //@ts-ignore
    return this.test({
        message: () => i18n.t("Validations.Required"),
        name: YUP_MIXED_REQUIRED_IF,
        exclusive: true,
        test(value: any) {
            const fieldsValueExists =
                typeof fields === "string"
                    ? [!!get(this.parent, fields)]
                    : (fields || []).map((field) => !!get(this.parent, field));
            return !fieldsValueExists.includes(true) || !!value;
        },
    });
});

// All referenced fields are empty
const YUP_MIXED_REQUIRED_IF_EMPTY = "requiredIfEmpty";

//@ts-ignore
// https://github.com/jquense/yup/issues/2058
Yup.addMethod(Yup.Schema, YUP_MIXED_REQUIRED_IF_EMPTY, function requiredIfEmpty(fields: string | string[]) {
    //@ts-ignore
    return this.test({
        message: () => i18n.t("Validations.Required"),
        name: YUP_MIXED_REQUIRED_IF_EMPTY,
        exclusive: true,
        test(value: any) {
            const fieldsValueExists =
                typeof fields === "string"
                    ? [!!this.parent[fields]]
                    : (fields || []).map((field) => !!this.parent[field]);
            return !fieldsValueExists.includes(false) || !!value;
        },
    });
});

// Yup string validator

// add Yup phone number validator
const YUP_PHONE_METHOD = "phone";
Yup.addMethod(Yup.string, YUP_PHONE_METHOD, function phone() {
    return this.test({
        message: () => i18n.t("Validations.Phone"),
        name: YUP_PHONE_METHOD,
        exclusive: true,
        test(value: string) {
            if (!value) return true;
            if (value.length <= 5) return false;
            return isValidPhoneNumber(value, defaultCountry);
        },
    });
});

// add Yup hex validator
const YUP_HEX_METHOD = "hex";
Yup.addMethod(Yup.string, YUP_HEX_METHOD, function hex(params: HexProps) {
    return this.test({
        message: () => i18n.t("Validations.Hex"),
        name: YUP_HEX_METHOD,
        exclusive: true,
        test(value: string) {
            if (!value) return true;
            if (typeof value !== "string") return false;
            if (!value.startsWith("#")) return false;
            value = convertHexToSixDigit(value);
            if (value.length !== 7) return false;
            if (isNaN(Number("0x" + value.substring(1)))) {
                return false;
            }
            if (
                params &&
                (params.minimalSaturation !== undefined ||
                    params.minimalLightness !== undefined ||
                    params.maximalLightness !== undefined)
            ) {
                const hsl = hexToHSL(value);
                const suggestedHsl: HSL = [hsl[0], hsl[1], hsl[2]];
                let hasError = false;
                let errorMessage;
                if (params.minimalSaturation !== undefined) {
                    if (hsl[1] <= params.minimalSaturation) {
                        hasError = true;
                        errorMessage = fakeI18n.t("Validations.ColorMoreSaturated");
                        suggestedHsl[1] = params.minimalSaturation + 1;
                    }
                }
                if (params.minimalLightness !== undefined) {
                    if (hsl[2] <= params.minimalLightness) {
                        hasError = true;
                        errorMessage = fakeI18n.t("Validations.ColorTooDark");
                        suggestedHsl[2] = params.minimalLightness + 1;
                    }
                }
                if (params.maximalLightness !== undefined) {
                    if (hsl[2] >= params.maximalLightness) {
                        hasError = true;
                        errorMessage = fakeI18n.t("Validations.ColorTooLight");
                        suggestedHsl[2] = params.maximalLightness - 1;
                    }
                }
                if (hasError) {
                    return this.createError({
                        message: i18n.t(errorMessage, { color: hslToHex(...suggestedHsl) }),
                    });
                }
            }
            return true;
        },
    });
});

// add Yup bce number validator
const YUP_BCE_METHOD = "bceNumber";
Yup.addMethod(Yup.string, YUP_BCE_METHOD, function bceNumber() {
    return this.test({
        message: () => i18n.t("Validations.Format"),
        name: YUP_BCE_METHOD,
        exclusive: true,
        test(value: string) {
            if (!value) return true;

            const formattedValue = value.replace(/\./g, "");

            const nr = Number(formattedValue.substring(0, formattedValue.length - 2));
            const check = Number(formattedValue.substring(formattedValue.length - 2));

            const validation = 97 - (nr % 97);

            return validation === check;
        },
    });
});

// add Yup exact lenght validator
const YUP_LENGTH_OR_EMPTY_METHOD = "exactLength";
const exactLengthMessage = ({ length }) => i18n.t("Validations.Length", { number: length });
Yup.addMethod(Yup.string, YUP_LENGTH_OR_EMPTY_METHOD, function exactLength(length: number) {
    return this.test({
        message: exactLengthMessage,
        name: YUP_LENGTH_OR_EMPTY_METHOD,
        exclusive: true,
        params: { length },
        test(value: Yup.Maybe<string>) {
            return !value || value.length === length;
        },
    });
});

// add Yup email validator

const rawYupEmailValidator = Yup.string().email();
const YUP_EMAIL_METHOD = "email";
Yup.addMethod(Yup.string, YUP_EMAIL_METHOD, function emailValidator() {
    return this.test({
        message: () => i18n.t("Validations.Email"),
        name: YUP_EMAIL_METHOD,
        exclusive: true,
        test: async function (value: string) {
            return (
                !value || ((await rawYupEmailValidator.isValid(value)) && (await dataCheckApi.queryCheckEmail(value)))
            );
        },
    });
});

// Yup number validator

// add amount validator
const YUP_AMOUNT_METHOD = "amount";
const amountMessage = ({ min, max }) => i18n.t("Validations.NumberAmount", { min, max });
Yup.addMethod(
    Yup.number,
    YUP_AMOUNT_METHOD,
    function amount(
        { min, max }: { min: number; max: number } = {
            min: 0,
            max: 100000000,
        },
    ) {
        return this.test({
            message: amountMessage,
            name: YUP_AMOUNT_METHOD,
            params: { min, max },
            exclusive: false,
            test(value: number) {
                return (!value && value !== 0) || (value >= min && value <= max);
            },
        });
    },
);

// add currency string transform to number
const YUP_CURRENCY_TRANSFORM_METHOD = "currencyTransform";
Yup.addMethod(Yup.number, YUP_CURRENCY_TRANSFORM_METHOD, function currencyTransform() {
    return this.transform((_value, originalValue) => {
        if (typeof originalValue === "string") {
            const cleanValue = originalValue.replaceAll(" ", "").replace("€", "").replace(",", ".");
            return cleanValue === "" ? null : parseFloat(cleanValue);
        }
        return originalValue;
    });
});

// Yup date validator

// add date present or past validator
const YUP_DATE_PRESENT_OR_PAST_METHOD = "presentOrPast";
Yup.addMethod(Yup.date, YUP_DATE_PRESENT_OR_PAST_METHOD, function presentOrPast() {
    return this.test({
        message: () => i18n.t("Validations.DatePresentOrPast"),
        name: YUP_DATE_PRESENT_OR_PAST_METHOD,
        exclusive: true,
        test(value: Date) {
            return !value || isPast(value) || isSameDay(value, new Date());
        },
    });
});

// add birthdate validator
const YUP_DATE_BIRTHDATE = "birthdate";
Yup.addMethod(Yup.date, YUP_DATE_BIRTHDATE, function birthdate() {
    return this.test({
        message: () => i18n.t("Validations.Date"),
        name: YUP_DATE_BIRTHDATE,
        exclusive: true,
        test(value: Date) {
            const minDate = new Date();
            minDate.setFullYear(minDate.getFullYear() - 135);
            return !value || ((isPast(value) || isSameDay(value, new Date())) && !isBefore(value, minDate));
        },
    });
});

// add date future validator
const YUP_DATE_FUTURE_METHOD = "onFuture";
Yup.addMethod(Yup.date, YUP_DATE_FUTURE_METHOD, function onFuture() {
    return this.test({
        message: () => i18n.t("Validations.DateFuture"),
        name: YUP_DATE_FUTURE_METHOD,
        exclusive: true,
        test(value: Date) {
            return !value || isFuture(value);
        },
    });
});

// add date year prior to X years validator.
const YUP_DATE_YEAR_PRIOR_TO_X_METHOD = "onLastXYears";
const onLastXYearsMessage = ({ years }) => i18n.t("Validations.DatePriorTo", { year: years });
Yup.addMethod(Yup.date, YUP_DATE_YEAR_PRIOR_TO_X_METHOD, function onLastXYears(years: number) {
    return this.test({
        message: onLastXYearsMessage,
        name: YUP_DATE_YEAR_PRIOR_TO_X_METHOD,
        exclusive: true,
        params: { years },
        test(value: Date) {
            const minDate = new Date();
            minDate.setFullYear(minDate.getFullYear() - years);
            return !value || !isBefore(value, minDate);
        },
    });
});

// add date min X years old validator.
const YUP_DATE_MIN_X_YEARS_OLD_METHOD = "minXYearsOld";
const minXYearsOldMessage = ({ years }) => i18n.t("Validations.MinXYearsOld", { years });
Yup.addMethod(
    Yup.date,
    YUP_DATE_MIN_X_YEARS_OLD_METHOD,
    function minXYearsOld(years: number, dateToCompare: Date | string, message?: string) {
        return this.test({
            message: message || minXYearsOldMessage,
            name: YUP_DATE_MIN_X_YEARS_OLD_METHOD,
            exclusive: true,
            params: { years },
            test(value: Date) {
                let compareDate;
                if (typeof dateToCompare === "string") {
                    const date = this.parent[dateToCompare];
                    compareDate = !date || differenceInYears(value, date) >= years;
                } else {
                    const diffInYears = dateToCompare
                        ? differenceInYears(value, dateToCompare)
                        : differenceInYears(new Date(), value);
                    compareDate = diffInYears >= years;
                }
                return !value || compareDate;
            },
        });
    },
);

// add date day after date of permit validator.
const YUP_DATE_MIN_DAY_AFTER_METHOD = "minDayAfter";

const minDayAfterMessage = ({ minDate }) =>
    i18n.t("Validations.MinDate", { number: defaultDateFormat(minDate as Date) });
Yup.addMethod(
    Yup.date,
    YUP_DATE_MIN_DAY_AFTER_METHOD,
    function minDayAfter(dateRef: string, { allowSameDay } = { allowSameDay: false }) {
        return this.test({
            name: YUP_DATE_MIN_DAY_AFTER_METHOD,
            exclusive: true,
            test(value: Date) {
                if (!value || !(dateRef in this.parent)) {
                    return true;
                }
                let minDate = this.parent[dateRef];

                if (!minDate || !isValid(minDate)) {
                    return true;
                }

                if (allowSameDay) {
                    minDate = subDays(minDate, 1);
                }

                if (isAfter(value, minDate)) {
                    return true;
                }

                return this.createError({
                    message: minDayAfterMessage({ minDate }),
                });
            },
        });
    },
);

const YUP_ARRAY_UNIQUE_METHOD = "unique";
const uniqueMessage = ({ fieldLabel }) =>
    i18n.t("Validations.DuplicatedValue", { field: i18n.t(fieldLabel).toLowerCase() });
Yup.addMethod(Yup.array, YUP_ARRAY_UNIQUE_METHOD, function unique(field, fieldLabel) {
    return this.test({
        message: uniqueMessage,
        name: YUP_ARRAY_UNIQUE_METHOD,
        exclusive: true,
        params: { fieldLabel },
        test(array: any[]) {
            const arrayValues = array.map((obj) => obj[field]);
            const nonEmptyData = arrayValues.filter(Boolean);
            const uniqueData = [...new Set(nonEmptyData)];
            const isUnique = nonEmptyData.length === uniqueData.length;
            if (isUnique) {
                return true;
            }
            const index = nonEmptyData.findIndex((value, i) => nonEmptyData.indexOf(value) !== i);
            return this.createError({
                path: `${this.path}.${index}.${field}`,
            });
        },
    });
});

// Overwrite defaults validations

// Date
// add date between valid range validator (01/01/1900 - 31/12/ present year + 100)
export const defaultMinDate = new Date(1900, 0, 1);
export const defaultMaxDate = new Date(new Date().getFullYear() + 100, 11, 31);
Yup.addMethod(Yup.date, "typeError", function () {
    return this.test({
        name: "typeError",
        exclusive: true,
        test: function (value: Date) {
            if (value !== undefined && !this.schema.isType(value)) {
                return this.createError({
                    message: i18n.t("Validations.Date"),
                    params: {
                        type: this.schema._type,
                    },
                });
            }

            if (isBefore(value, defaultMinDate) || isAfter(value, defaultMaxDate)) {
                return this.createError({
                    message: i18n.t("Validations.BetweenDates", {
                        min: defaultDateFormat(defaultMinDate),
                    }),
                });
            }

            return true;
        },
    });
});
